/**
 * @file WidgetAgendaPanel.tsx
 * @brief The widget's agenda popup: the meeting's agenda notes, read only,
 *        one at a time, plus the decisions and to-dos marked so far.
 *
 * @details
 * Read only by the same decision as the recorder's agenda panel (24-08-2026):
 * what is said is already in the transcript. B49 (DES 02-11-2026): when the
 * session carries `agendaChecks`, each item gets a check box (tap = done,
 * again = undo), sent to the recorder page as `agenda-tick`; the page owns
 * the ticks and their recording-clock time. Older pages send none: read only. ‹ › and dots move between notes
 * when the meeting has several (up to MAX_AGENDA_NOTES). Says "agenda", not
 * "note", since agendas became their own kind (DES 12-10-2026).
 *
 * @author DES
 * @date 02-10-2026
 */

import { useState, type ReactNode } from "react";
import { Bookmark, Check, ChevronLeft, ChevronRight, CircleCheckBig, LoaderCircle, X } from "lucide-react";
import { AgendaFlag } from "@/components/icons/AgendaFlag";
import { cn } from "@/shared/theme/cn";
import { desktopShell, type DesktopSession } from "@/shared/desktop/desktopShell";
import { useNote } from "@/features/notes/api";
import { notePointLevels, notePoints } from "@/features/notes/schemas";
import { pointLabels } from "@/features/notes/pointLevels";
import { W } from "./widgetUi";

export function PanelHeader({
  icon,
  title,
  right,
}: {
  icon: ReactNode;
  title: string;
  right?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      {icon}
      <span className="font-medium">{title}</span>
      <span className="flex-1" />
      {right}
      <button
        type="button"
        aria-label="Close"
        onClick={() => desktopShell()?.togglePanel?.(null)}
        className={cn("inline-flex h-6 w-6 items-center justify-center rounded-full", W.chip)}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function WidgetAgendaPanel({ session }: { session: DesktopSession }) {
  const ids = session.agendaNoteIds ?? [];
  const [index, setIndex] = useState(0);
  const at = Math.min(index, Math.max(0, ids.length - 1));
  const note = useNote(ids[at] ?? null);
  const points = note.data ? notePoints(note.data) : [];
  // Sub-points (DES 12-10-2026): lettered and indented under their point.
  const levels = note.data ? notePointLevels(note.data, points) : null;
  const labels = pointLabels(levels, points.length);
  const marks = session.bookmarks ?? [];
  // B49: this note's check boxes, when the recorder page sends them.
  const allChecks = session.agendaChecks ?? null;
  const checks = allChecks ? allChecks.filter((c) => c.noteId === ids[at]) : null;
  const done = allChecks ? allChecks.filter((c) => c.checked).length : 0;

  return (
    <div className={cn("flex flex-col gap-2 rounded-[24px] px-3.5 py-3 text-[13px]", W.surface)}>
      <PanelHeader
        icon={<AgendaFlag className="h-4 w-4 text-brand" />} // DES 08-10-2026: app icon, was ListChecks
        title="Agenda"
        right={
          <>
          {allChecks && allChecks.length > 0 ? (
            <span className={cn("text-xs tabular-nums", W.muted)}>
              {done}/{allChecks.length}
            </span>
          ) : null}
          {ids.length > 1 ? (
            <span className="flex items-center gap-1">
              {ids.map((id, i) => (
                <span
                  key={id}
                  className={cn("h-1.5 w-1.5 rounded-full", i === at ? "bg-brand" : "bg-[#5f5e5a]")}
                />
              ))}
            </span>
          ) : null}
          </>
        }
      />

      {ids.length === 0 ? (
        <p className={cn("py-2 text-xs", W.muted)}>
          No agenda for this meeting. Agendas are picked when the meeting is set up.
        </p>
      ) : (
        <>
          <div className="flex items-center gap-2">
            {ids.length > 1 ? (
              <button
                type="button"
                aria-label="Previous agenda"
                disabled={at === 0}
                onClick={() => setIndex(at - 1)}
                className={cn("rounded-full p-0.5 disabled:opacity-30", W.muted)}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            ) : null}
            <span className="min-w-0 flex-1 truncate text-center">
              {note.data?.title?.trim() || (note.isLoading ? "" : "Untitled agenda")}
            </span>
            {ids.length > 1 ? (
              <button
                type="button"
                aria-label="Next agenda"
                disabled={at >= ids.length - 1}
                onClick={() => setIndex(at + 1)}
                className={cn("rounded-full p-0.5 disabled:opacity-30", W.muted)}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          <div className="max-h-[220px] space-y-1.5 overflow-y-auto pr-1">
            {checks && checks.length > 0 ? (
              checks.map((c) => (
                <button
                  key={`${c.noteId}:${c.index}`}
                  type="button"
                  role="checkbox"
                  aria-checked={c.checked}
                  disabled={session.status === "busy"}
                  onClick={() =>
                    desktopShell()?.sendCommand?.({ type: "agenda-tick", key: `${c.noteId}:${c.index}` })
                  }
                  className={cn(
                    "flex w-full items-start gap-2 text-left text-xs leading-relaxed text-[#d3d1c7]",
                    c.parentText ? "pl-5" : "",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "mt-[3px] flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[4px] border",
                      c.checked ? "border-brand bg-brand text-white" : "border-[#5f5e5a]",
                    )}
                  >
                    {c.checked ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : null}
                  </span>
                  <span className={cn("min-w-0", c.checked && W.muted)}>{c.text}</span>
                </button>
              ))
            ) : note.isLoading ? (
              <LoaderCircle className={cn("mx-auto h-4 w-4 animate-spin", W.muted)} />
            ) : points.length === 0 ? (
              <p className={cn("text-xs", W.muted)}>This agenda has no points yet.</p>
            ) : (
              points.map((p, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex gap-2 text-xs leading-relaxed text-[#d3d1c7]",
                    levels?.[i] ? "pl-5" : "",
                  )}
                >
                  <span className={cn("w-3 shrink-0 text-right", W.muted)}>{labels[i]}</span>
                  <span className="min-w-0">{p}</span>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {marks.length > 0 ? (
        <div className="flex flex-col gap-1.5 border-t border-[#3A3938] pt-2">
          <span className={cn("text-xs", W.muted)}>Marked so far</span>
          <div className="max-h-[96px] space-y-1 overflow-y-auto pr-1">
            {marks.map((m, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-[#d3d1c7]">
                {m.type === "decision" ? (
                  <Bookmark className="h-3.5 w-3.5 fill-brand text-brand" />
                ) : (
                  <CircleCheckBig className="h-3.5 w-3.5 text-brand" />
                )}
                <span className="font-mono tabular-nums">{m.timestamp}</span>
                {m.type === "decision" ? "Decision" : "To-do"}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
